import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisDialogForm from "../../components/formdialog";
import PdfViewer from "../../components/controls/pdfviewer";
import { api, blobUrl, classifyByDate, docLink, fileToBase64, toInputDate, tone, JIS_START } from "./hctc_common";
import "./hctc.css";

// Master application form (spec 3): sections A-F for CTC, decree, default fee and case search requests (screen svc_apply).
const STEPS = [
  ["A", "Applicant"],
  ["B", "Request"],
  ["C", "Purpose"],
  ["D", "Uploads"],
  ["E", "Media Instruments"],
  ["F", "Declaration"],
];

const BLANK = {
  id: 0, requestType: "", applicantType: "", fullName: "", lawFirmName: "", enrollmentNumber: "", email: "", mobile: "", address: "",
  caseId: null, historicalCaseId: null, caseNumber: "", suitNumber: "", legacyCaseNumber: "", caseTitle: "", originalFilingDate: "",
  divisionId: "", courtTypeId: 1, judgeName: "", registrarName: "", physicalFileNumber: "", archiveReferenceNumber: "",
  purpose: "", remarks: "", judgmentDate: "", startDate: "", endDate: "", daysRequested: "",
  searchStartYear: "", searchEndYear: "", searchCaseTypeId: "", searchPartyName: "", isHistoricalSearch: false,
};

const NUMS = ["divisionId", "courtTypeId", "daysRequested", "searchStartYear", "searchEndYear", "searchCaseTypeId"];
const DATES = ["originalFilingDate", "judgmentDate", "startDate", "endDate"];

const UPLOADS = [
  ["application_letter", "Application letter (PDF)", true],
  ["supporting", "Supporting documents", false],
];

export default function ServiceApply() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, frequentData, setIsExpired } = useContext(ShareContext);
  const [cfg, setCfg] = useState(null);
  const [a, setA] = useState(BLANK);
  const [step, setStep] = useState("A");
  const [docs, setDocs] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [chosen, setChosen] = useState({}); // type -> instrument id
  const [declared, setDeclared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [status, setStatus] = useState("Draft");
  const [search, setSearch] = useState({ text: "", party: "" });
  const [results, setResults] = useState(null);
  const [preview, setPreview] = useState("");
  const [confirmNoSig, setConfirmNoSig] = useState(false);
  const [done, setDone] = useState(null);

  const rt = useMemo(() => cfg?.requestTypes?.find((r) => r.code === a.requestType), [cfg, a.requestType]);
  const isCase = rt?.needsCase;
  const isSearch = a.requestType === "case_search";
  const isNonCase = a.requestType === "non_case";
  const isDecree = a.requestType === "decree_absolute" || a.requestType === "decree_nisi";
  const isDefault = a.requestType === "default_fee";
  const classification = isSearch
    ? a.isHistoricalSearch || (a.searchStartYear && Number(a.searchStartYear) < 2013)
      ? "Historical"
      : "JIS"
    : a.historicalCaseId
      ? "Historical"
      : isNonCase && !a.originalFilingDate
        ? "Non-Case"
        : classifyByDate(a.originalFilingDate);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [c, p] = await Promise.all([api(token, setIsExpired, "serviceapp/config"), api(token, setIsExpired, "serviceapp/prefill")]);
      if (c.code === 0) setCfg(c);
      let inst = [];
      if (p.code === 0) {
        inst = p.instruments || [];
        setInstruments(inst);
      }
      if (id) {
        const d = await api(token, setIsExpired, `serviceapp/${id}`);
        if (d.code === 0) {
          const it = d.item;
          const row = {};
          Object.keys(BLANK).forEach((k) => (row[k] = it[k] ?? BLANK[k]));
          DATES.forEach((k) => (row[k] = toInputDate(it[k])));
          setA(row);
          setStatus(it.status);
          setDocs(d.documents || []);
          const pick = {};
          (d.instruments || []).forEach((m) => {
            if (inst.some((i) => i.id === m.instrumentId)) pick[m.instrumentType] = m.instrumentId;
          });
          setChosen(pick);
          if (it.status === "Rework Requested") setMsg(`Rework requested: ${it.lastComment || ""}`);
        } else setErr(d.status || "Unable to load the application");
      } else if (p.code === 0) {
        setA({ ...BLANK, ...p.applicant, requestType: "" });
        const pick = {};
        inst.filter((i) => i.isDefault).forEach((i) => (pick[i.instrumentType] = i.id));
        setChosen(pick); // the applicant's active default instruments load automatically (spec 5.9)
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const on = (e) => {
    const { name, value, type, checked } = e.target;
    setA({ ...a, [name]: type === "checkbox" ? checked : value });
  };

  const payload = () => {
    const b = { ...a, id: Number(a.id) || 0 };
    NUMS.forEach((k) => (b[k] = b[k] === "" || b[k] === null ? null : Number(b[k])));
    DATES.forEach((k) => (b[k] = b[k] || null));
    return b;
  };

  const save = async (quiet) => {
    if (!a.requestType) {
      setErr("Select the request type (Section B).");
      setStep("B");
      return null;
    }
    setErr("");
    setLoading(true);
    const d = await api(token, setIsExpired, "serviceapp/save", { method: "POST", body: payload() });
    setLoading(false);
    if (d.code === 0) {
      setA((x) => ({ ...x, id: d.id, judgeName: d.judgeName || x.judgeName, registrarName: d.registrarName || x.registrarName, daysRequested: d.daysRequested ?? x.daysRequested }));
      if (!id && !a.id) window.history.replaceState(null, "", `/service-applications/apply/${d.id}`);
      if (!quiet) setMsg("Draft saved");
      return d.id;
    }
    if (d.code !== -10) setErr(d.status || "Unable to save");
    return null;
  };

  const go = async (s) => {
    if (["C", "D", "E", "F"].includes(s) && a.requestType) {
      const ok = await save(true);
      if (!ok) return;
    }
    setStep(s);
    setMsg("");
  };

  // Section B: search historical and JIS records (spec 4.2)
  const runSearch = async () => {
    setErr("");
    setLoading(true);
    const d = await api(token, setIsExpired, "historical/unified-search", {
      method: "POST",
      body: { text: search.text || null, partyName: search.party || null, divisionId: a.divisionId ? Number(a.divisionId) : null },
    });
    setLoading(false);
    if (d.code === 0) setResults(d.results || []);
    else if (d.code !== -10) setErr(d.status || "Search failed");
  };

  const pickResult = (r) => {
    setA({
      ...a,
      caseId: r.caseId || null,
      historicalCaseId: r.historicalCaseId || null,
      caseNumber: r.number || "",
      suitNumber: r.legacySuitNumber || a.suitNumber,
      legacyCaseNumber: r.legacyCaseNumber || "",
      caseTitle: r.title || "",
      originalFilingDate: toInputDate(r.filingDate),
      divisionId: r.divisionId || a.divisionId,
      courtTypeId: r.courtTypeId || 1,
      judgeName: r.judgeName || "",
      registrarName: r.registrarName || "",
      physicalFileNumber: r.physicalFileNumber || "",
      archiveReferenceNumber: r.archiveReferenceNumber || "",
    });
    setResults(null);
  };

  const clearPick = () => setA({ ...a, caseId: null, historicalCaseId: null });

  useEffect(() => {
    if (!isDefault || !a.startDate || !a.endDate || a.daysRequested) return;
    (async () => {
      const d = await api(token, setIsExpired, `serviceapp/chargeable-days?start=${a.startDate}&end=${a.endDate}`);
      if (d.code === 0) setA((x) => ({ ...x, daysRequested: d.days }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.startDate, a.endDate, isDefault]);

  const upload = async (category, files, title) => {
    const appId = a.id || (await save(true));
    if (!appId) return;
    for (const file of files) {
      setLoading(true);
      const base64 = await fileToBase64(file);
      const d = await api(token, setIsExpired, `serviceapp/upload/${appId}`, { method: "POST", body: { base64, fileName: file.name, category, title: title || null } });
      setLoading(false);
      if (d.code !== 0 && d.code !== -10) {
        setErr(`${file.name}: ${d.status || "upload failed"}`);
        break;
      }
    }
    const r = await api(token, setIsExpired, `serviceapp/${appId}`);
    if (r.code === 0) setDocs(r.documents || []);
  };

  const removeDoc = async (doc) => {
    const d = await api(token, setIsExpired, `serviceapp/remove-doc/${doc.id}`, { method: "POST" });
    if (d.code === 0) setDocs(docs.filter((x) => x.id !== doc.id));
    else setErr(d.status || "Unable to remove");
  };

  const instrumentIds = () => Object.values(chosen).filter(Boolean).map(Number);

  const onPreview = async () => {
    const appId = a.id || (await save(true));
    if (!appId) return;
    setLoading(true);
    try {
      setPreview(await blobUrl(token, `serviceapp/preview/${appId}`, { declarationAccepted: declared, instrumentIds: instrumentIds() }));
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };

  const submit = async (noSig) => {
    const appId = await save(true);
    if (!appId) return;
    setErr("");
    setLoading(true);
    const d = await api(token, setIsExpired, `serviceapp/submit/${appId}`, {
      method: "POST",
      body: { declarationAccepted: declared, instrumentIds: instrumentIds(), confirmNoSignature: !!noSig },
    });
    setLoading(false);
    if (d.code === 0) setDone(d);
    else if (d.code === -6) setConfirmNoSig(true);
    else if (d.code !== -10) setErr(d.status || "Unable to submit");
  };

  const locked = status !== "Draft" && status !== "Rework Requested";

  if (done) {
    return (
      <div className="hctc-page">
        <div className="hctc-card" style={{ maxWidth: 640 }}>
          <h2>Application submitted</h2>
          <div className="hctc-kv" style={{ margin: "12px 0" }}>
            <div className="k">Application number</div>
            <div className="v">{done.applicationNumber}</div>
            <div className="k">Tracking number</div>
            <div className="v">{done.trackingNumber}</div>
            <div className="k">Classification</div>
            <div className="v">{done.classification}</div>
            <div className="k">Status</div>
            <div className="v">
              <span className={`hctc-pill ${tone(done.status)}`}>{done.status}</span>
            </div>
          </div>
          {done.status === "Historical Case Verification Required" && (
            <div className="hctc-info">The case is historical (or its filing date is not known). The registry will verify it before your request is processed.</div>
          )}
          <div className="hctc-actions">
            <JisButton onClick={() => navigate(`/service-applications/${a.id}`)}>Open application</JisButton>
            <JisButton variant="outline" onClick={() => navigate("/service-applications")}>
              My applications
            </JisButton>
          </div>
        </div>
      </div>
    );
  }

  if (locked && a.id) {
    return (
      <div className="hctc-page">
        <div className="hctc-warn">This application is {status} and can no longer be edited.</div>
        <JisButton onClick={() => navigate(`/service-applications/${a.id}`)}>Open application</JisButton>
      </div>
    );
  }

  const field = (name, label, opts = {}) => (
    <div className={`hctc-field ${opts.span || ""}`}>
      <label>
        {label}
        {opts.req && <span className="req">*</span>}
      </label>
      {opts.area ? (
        <textarea name={name} value={a[name] ?? ""} onChange={on} />
      ) : (
        <input type={opts.type || "text"} name={name} value={a[name] ?? ""} onChange={on} min={opts.min} max={opts.max} readOnly={opts.readOnly} />
      )}
      {opts.hint && <div className="hint">{opts.hint}</div>}
    </div>
  );

  const docsOf = (cat) => docs.filter((d) => d.category === cat);
  const uploadRow = (cat, label, req, multi = true) => (
    <div className="hctc-card" key={cat}>
      <div className="head">
        <h3>
          {label}
          {req && <span style={{ color: "#c62828" }}> *</span>}
        </h3>
        <label className="jis-button outline" style={{ cursor: "pointer" }}>
          Add file
          <input
            type="file"
            multiple={multi}
            accept={cat === "application_letter" ? ".pdf" : ".pdf,.png,.jpg,.jpeg"}
            style={{ display: "none" }}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = "";
              if (files.length) upload(cat, files);
            }}
          />
        </label>
      </div>
      {docsOf(cat).length === 0 ? (
        <div className="hctc-muted">No file yet</div>
      ) : (
        docsOf(cat).map((d) => (
          <div key={d.id} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
            <a href={docLink(d.url)} target="_blank" rel="noreferrer">
              {d.title}
            </a>
            <span className="hctc-muted">{d.pageCount} page(s)</span>
            <JisButton variant="plain" onClick={() => removeDoc(d)}>
              Remove
            </JisButton>
          </div>
        ))
      )}
    </div>
  );

  const types = cfg?.instrumentTypes || [];
  const byType = (code) => instruments.filter((i) => i.instrumentType === code);

  return (
    <div className="hctc-page">
      <div className="hctc-toolbar">
        <div className="left">
          <JisButton variant="plain" onClick={() => navigate("/service-applications")}>
            Back
          </JisButton>
          <div>
            <h2>{rt ? `${rt.title} Application` : "New Application"}</h2>
            <div className="hctc-sub">
              Classification: <span className={`hctc-pill ${tone(classification)}`}>{classification}</span>
              {classification === "Unknown" && isCase && " The filing date is not known: the registry will verify the case first."}
            </div>
          </div>
        </div>
        <div className="right">
          <JisButton variant="outline" onClick={() => save(false)}>
            Save draft
          </JisButton>
        </div>
      </div>

      <div className="hctc-steps">
        {STEPS.map(([k, t]) => (
          <div key={k} className={`hctc-step ${step === k ? "active" : ""}`} onClick={() => go(k)}>
            {k}. {t}
          </div>
        ))}
      </div>
      {msg && <div className={msg.startsWith("Rework") ? "hctc-warn" : "hctc-ok"}>{msg}</div>}
      {err && <div className="hctc-err">{err}</div>}

      {step === "A" && (
        <div className="hctc-card">
          <div className="hctc-section-title">Section A: Applicant information</div>
          <div className="hctc-grid">
            <div className="hctc-field">
              <label>
                Applicant type<span className="req">*</span>
              </label>
              <select name="applicantType" value={a.applicantType} onChange={on}>
                <option value="">Select</option>
                {(cfg?.applicantTypes || []).map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>
            {field("fullName", "Full name", { req: true, span: "span2" })}
            {field("lawFirmName", "Law firm name")}
            {field("enrollmentNumber", "Supreme Court enrollment number", { req: a.applicantType === "Legal Counsel" })}
            {field("email", "Email address", { req: true, type: "email" })}
            {field("mobile", "Mobile number", { req: true })}
            {field("address", "Address", { req: true, area: true, span: "full" })}
          </div>
          <div className="hctc-actions" style={{ marginTop: 12 }}>
            <JisButton onClick={() => setStep("B")}>Next</JisButton>
          </div>
        </div>
      )}

      {step === "B" && (
        <div className="hctc-card">
          <div className="hctc-section-title">Section B: Request information</div>
          <div className="hctc-grid">
            <div className="hctc-field span2">
              <label>
                Request type<span className="req">*</span>
              </label>
              <select name="requestType" value={a.requestType} onChange={on}>
                <option value="">Select</option>
                {["CTC", "Decree", "Default Fee", "Case Search"].map((cat) => (
                  <optgroup key={cat} label={cat}>
                    {(cfg?.requestTypes || [])
                      .filter((r) => r.category === cat)
                      .map((r) => (
                        <option key={r.code} value={r.code}>
                          {r.title}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          {(isCase || isNonCase) && (
            <>
              <div className="hctc-card" style={{ marginTop: 12 }}>
                <div className="head">
                  <h3>Find the case</h3>
                  {(a.caseId || a.historicalCaseId) && (
                    <span>
                      <span className="hctc-pill good">Record selected</span>
                      <JisButton variant="plain" onClick={clearPick}>
                        Clear
                      </JisButton>
                    </span>
                  )}
                </div>
                <div className="hctc-sub">Searches JIS cases and historical (pre 2 October 2013) records. If nothing matches, fill in the details below.</div>
                <div className="hctc-grid">
                  <div className="hctc-field span2">
                    <label>Case / suit / legacy number or title</label>
                    <input value={search.text} onChange={(e) => setSearch({ ...search, text: e.target.value })} onKeyDown={(e) => e.key === "Enter" && runSearch()} />
                  </div>
                  <div className="hctc-field">
                    <label>Party name</label>
                    <input value={search.party} onChange={(e) => setSearch({ ...search, party: e.target.value })} onKeyDown={(e) => e.key === "Enter" && runSearch()} />
                  </div>
                  <div className="hctc-field" style={{ display: "flex", alignItems: "flex-end" }}>
                    <JisButton onClick={runSearch}>Search</JisButton>
                  </div>
                </div>
                {results && (
                  <div style={{ maxHeight: 300, overflowY: "auto", marginTop: 10 }}>
                    {results.length === 0 ? (
                      <div className="hctc-muted">No matching record. Enter the case details below; the registry will verify them.</div>
                    ) : (
                      <table className="case-table hctc-table">
                        <thead>
                          <tr>
                            <th>Number</th>
                            <th>Title</th>
                            <th>Filed</th>
                            <th>Division</th>
                            <th>Record</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {results.map((r) => (
                            <tr key={r.key}>
                              <td>
                                {r.number}
                                <div className="hctc-muted">{r.reference}</div>
                              </td>
                              <td className="wrap">
                                {r.title}
                                {r.parties?.length > 0 && <div className="hctc-muted">{r.parties.join("; ")}</div>}
                              </td>
                              <td>{r.filingDate ? toInputDate(r.filingDate) : "Unknown"}</td>
                              <td>{r.division || "-"}</td>
                              <td>
                                {(r.labels || []).map((l) => (
                                  <span key={l} className={`hctc-pill ${tone(l)}`}>
                                    {l}
                                  </span>
                                ))}
                              </td>
                              <td>
                                <JisButton variant="plain" onClick={() => pickResult(r)}>
                                  Select
                                </JisButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
              <div className="hctc-grid">
                {field("caseNumber", isNonCase ? "Related case number (if any)" : "Case number")}
                {!isNonCase && field("suitNumber", "Suit number")}
                {field("legacyCaseNumber", "Legacy case number")}
                {field("caseTitle", isNonCase ? "Document description" : "Case title", { req: !isNonCase, span: "span2" })}
                {field("originalFilingDate", "Original filing date", { type: "date", hint: a.originalFilingDate && a.originalFilingDate < JIS_START ? "Before 2 Oct 2013: historical case" : "Leave empty if not known" })}
                {!isNonCase && (
                  <div className="hctc-field">
                    <label>
                      Judicial division<span className="req">*</span>
                    </label>
                    <select name="divisionId" value={a.divisionId ?? ""} onChange={on}>
                      <option value="">Select</option>
                      {frequentData?.divisions?.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!isNonCase && field("judgeName", "Judge", { readOnly: !!(a.caseId || a.historicalCaseId), hint: a.caseId ? "From the case" : "" })}
                {!isNonCase && field("registrarName", "Registrar", { readOnly: !!a.caseId })}
                {!isNonCase && field("physicalFileNumber", "Physical file number")}
                {!isNonCase && field("archiveReferenceNumber", "Archive reference number")}
              </div>
            </>
          )}

          {isDecree && (
            <div className="hctc-grid" style={{ marginTop: 12 }}>
              <div className="hctc-field">
                <label>Decree type</label>
                <input value={rt?.title || ""} readOnly />
              </div>
              {field("judgmentDate", "Judgment date", { type: "date" })}
            </div>
          )}

          {isDefault && (
            <div className="hctc-grid" style={{ marginTop: 12 }}>
              {field("startDate", "Start date", { type: "date", req: true })}
              {field("endDate", "End date", { type: "date", req: true })}
              {field("daysRequested", "Number of days requested", { type: "number", req: true, hint: "Sundays and public holidays are not charged" })}
            </div>
          )}

          {isSearch && (
            <div className="hctc-grid" style={{ marginTop: 12 }}>
              {field("searchStartYear", "Search start year", { type: "number", req: true, min: 1900 })}
              {field("searchEndYear", "Search end year", { type: "number", req: true, min: 1900 })}
              <div className="hctc-field">
                <label>Case type</label>
                <select name="searchCaseTypeId" value={a.searchCaseTypeId ?? ""} onChange={on}>
                  <option value="">Any</option>
                  {frequentData?.casetype?.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.caseType1}
                    </option>
                  ))}
                </select>
              </div>
              <div className="hctc-field">
                <label>Division</label>
                <select name="divisionId" value={a.divisionId ?? ""} onChange={on}>
                  <option value="">Any</option>
                  {frequentData?.divisions?.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="hctc-field" style={{ display: "flex", alignItems: "flex-end" }}>
                <label className="hctc-check">
                  <input type="checkbox" name="isHistoricalSearch" checked={!!a.isHistoricalSearch} onChange={on} /> Historical case search
                </label>
              </div>
              {field("caseNumber", "Original case number")}
              {field("legacyCaseNumber", "Legacy case number")}
              {field("searchPartyName", "Party name")}
              {field("physicalFileNumber", "Physical file number")}
              {field("archiveReferenceNumber", "Archive reference number")}
              {a.searchStartYear && a.searchEndYear && (
                <div className="hctc-field full">
                  <div className="hctc-info">
                    Search coverage: {Number(a.searchEndYear) - Number(a.searchStartYear) + 1} year(s).{" "}
                    {Number(a.searchStartYear) <= 2013 && "Includes historical records (before 2 Oct 2013): digitized archive records and physical registry indexes. "}
                    {Number(a.searchEndYear) >= 2013 && "Includes JIS records (from 2 Oct 2013)."}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="hctc-actions" style={{ marginTop: 12 }}>
            <JisButton variant="outline" onClick={() => setStep("A")}>
              Previous
            </JisButton>
            <JisButton onClick={() => go("C")}>Next</JisButton>
          </div>
        </div>
      )}

      {step === "C" && (
        <div className="hctc-card">
          <div className="hctc-section-title">Section C: Purpose of request</div>
          <div className="hctc-grid">
            {field("purpose", "Purpose", { area: true, req: true, span: "full" })}
            {field("remarks", "Additional remarks", { area: true, span: "full" })}
          </div>
          <div className="hctc-actions" style={{ marginTop: 12 }}>
            <JisButton variant="outline" onClick={() => setStep("B")}>
              Previous
            </JisButton>
            <JisButton onClick={() => go("D")}>Next</JisButton>
          </div>
        </div>
      )}

      {step === "D" && (
        <div>
          <div className="hctc-section-title">Section D: Uploads</div>
          {UPLOADS.map(([c, l, r]) => uploadRow(c, l, r, c !== "application_letter"))}
          {isNonCase && uploadRow("original_document", "Original document", true)}
          {isNonCase && uploadRow("photocopy", "Photocopy to be certified", true)}
          {(classification === "Historical" || classification === "Unknown") && (
            <>
              <div className="hctc-info">
                Historical case: you may also upload previous court correspondence, old receipts, certified copies of legacy documents, registry extracts, previous
                judgments or orders, evidence of the case number or of party identity.
              </div>
              {uploadRow("historical_support", "Historical case supporting documents", false)}
            </>
          )}
          <div className="hctc-actions">
            <JisButton variant="outline" onClick={() => setStep("C")}>
              Previous
            </JisButton>
            <JisButton onClick={() => go("E")}>Next</JisButton>
          </div>
        </div>
      )}

      {step === "E" && (
        <div className="hctc-card">
          <div className="hctc-section-title">Section E: Applicant media instruments</div>
          <div className="hctc-sub">
            Only your active, valid and non-expired instruments are listed. They are applied to your application form only, never to court certification areas.{" "}
            <a href="/my-media-instruments" target="_blank" rel="noreferrer">
              Manage Media Instruments
            </a>
          </div>
          {!chosen.signature && <div className="hctc-warn">No signature is selected for the declaration.</div>}
          <div className="hctc-grid wide">
            {types
              .filter((t) => byType(t.code).length > 0)
              .map((t) => (
                <div key={t.code} className="hctc-card" style={{ marginBottom: 0 }}>
                  <label className="hctc-check" style={{ fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={!!chosen[t.code]}
                      onChange={(e) => setChosen({ ...chosen, [t.code]: e.target.checked ? byType(t.code).find((i) => i.isDefault)?.id || byType(t.code)[0].id : null })}
                    />
                    Apply {t.title}
                  </label>
                  {chosen[t.code] && (
                    <>
                      <select style={{ width: "100%", marginTop: 8, padding: 6, borderRadius: 8 }} value={chosen[t.code]} onChange={(e) => setChosen({ ...chosen, [t.code]: Number(e.target.value) })}>
                        {byType(t.code).map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.instrumentName} {i.isDefault ? "(default)" : ""}
                          </option>
                        ))}
                      </select>
                      {(() => {
                        const i = instruments.find((x) => x.id === chosen[t.code]);
                        if (!i) return null;
                        return (
                          <div style={{ display: "flex", gap: 10, marginTop: 8, alignItems: "center" }}>
                            {/\.(png|jpe?g)$/i.test(i.url) && <img className="hctc-thumb" src={docLink(i.url)} alt={i.instrumentName} />}
                            <div className="hctc-muted">
                              {i.verificationStatus}
                              {i.effectiveDate && <div>From {toInputDate(i.effectiveDate)}</div>}
                              {i.expiryDate && <div>Expires {toInputDate(i.expiryDate)}</div>}
                              <div>Placement: {t.placement}</div>
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              ))}
          </div>
          {instruments.length === 0 && <div className="hctc-muted">You have no active media instruments.</div>}
          <div className="hctc-actions" style={{ marginTop: 12 }}>
            <JisButton variant="outline" onClick={() => setStep("D")}>
              Previous
            </JisButton>
            <JisButton variant="outline" onClick={onPreview}>
              Preview application
            </JisButton>
            <JisButton onClick={() => go("F")}>Next</JisButton>
          </div>
        </div>
      )}

      {step === "F" && (
        <div className="hctc-card">
          <div className="hctc-section-title">Section F: Declaration</div>
          <label className="hctc-check" style={{ fontSize: 14 }}>
            <input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} />I declare that the information given in this application is true and
            complete to the best of my knowledge, and that the documents uploaded are genuine.
          </label>
          <div className="hctc-muted" style={{ marginTop: 8 }}>
            Signature: {chosen.signature ? instruments.find((i) => i.id === chosen.signature)?.instrumentName : "none selected"} · Submission date: today
          </div>
          <div className="hctc-actions" style={{ marginTop: 14 }}>
            <JisButton variant="outline" onClick={() => setStep("E")}>
              Previous
            </JisButton>
            <JisButton variant="outline" onClick={onPreview}>
              Preview
            </JisButton>
            <JisButton onClick={() => submit(false)} disabled={!declared}>
              Submit application
            </JisButton>
          </div>
        </div>
      )}

      {confirmNoSig && (
        <JisDialogForm title="No signature selected" onClose={() => setConfirmNoSig(false)} minWidth="30%">
          <div className="hctc-dialog-body">
            <p>No signature instrument is selected for the declaration. Submit without a signature?</p>
            <div className="hctc-actions">
              <JisButton
                onClick={() => {
                  setConfirmNoSig(false);
                  submit(true);
                }}
              >
                Submit without signature
              </JisButton>
              <JisButton
                variant="outline"
                onClick={() => {
                  setConfirmNoSig(false);
                  setStep("E");
                }}
              >
                Select a signature
              </JisButton>
            </div>
          </div>
        </JisDialogForm>
      )}
      {preview && (
        <JisDialogForm title="Application preview" onClose={() => setPreview("")}>
          <div style={{ height: "80vh", minWidth: "min(900px, 85vw)" }}>
            <PdfViewer pdfUrl={preview} />
          </div>
        </JisDialogForm>
      )}
      <BackLoader open={loading} />
    </div>
  );
}
